import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { graphqlRequest } from './graphql'
import type {
  DeliveryZoneFull as SharedDeliveryZoneFull,
  DeliveryFeeArea as SharedDeliveryFeeArea,
  DeliveryFeeAreaType,
  CreateDeliveryZoneInput as SharedCreateDeliveryZoneInput,
  UpdateDeliveryZoneInput as SharedUpdateDeliveryZoneInput,
  CreateDeliveryFeeAreaInput as SharedCreateDeliveryFeeAreaInput,
  UpdateDeliveryFeeAreaInput as SharedUpdateDeliveryFeeAreaInput,
} from '@shinkansen/graphql'

// Re-export types from shared package for convenience
export type { DeliveryFeeAreaType }

/**
 * Full delivery zone interface - using shared type from GraphQL schema
 */
export type DeliveryZoneFull = SharedDeliveryZoneFull

/**
 * Delivery fee area interface - using shared type from GraphQL schema
 */
export type DeliveryFeeArea = SharedDeliveryFeeArea

/**
 * Input for creating delivery zone - using shared type from GraphQL schema
 */
export type CreateDeliveryZoneInput = SharedCreateDeliveryZoneInput

/**
 * Input for updating delivery zone - using shared type from GraphQL schema
 */
export type UpdateDeliveryZoneInput = SharedUpdateDeliveryZoneInput

/**
 * Input for creating delivery fee area - using shared type from GraphQL schema
 */
export type CreateDeliveryFeeAreaInput = SharedCreateDeliveryFeeAreaInput

/**
 * Input for updating delivery fee area - using shared type from GraphQL schema
 */
export type UpdateDeliveryFeeAreaInput = SharedUpdateDeliveryFeeAreaInput

const DELIVERY_ZONE_FRAGMENT = `
  id
  name
  description
  boundaryWkt
  baseDeliveryFee
  minOrderAmount
  estimatedMinutesMin
  estimatedMinutesMax
  isActive
  createdAt
  updatedAt
`

const FEE_AREA_FRAGMENT = `
  id
  zoneId
  name
  description
  areaType
  fee
  boundaryWkt
  centerLat
  centerLng
  radiusKm
  isActive
  createdAt
  updatedAt
`

// Queries

export function useDeliveryZones() {
  return useQuery({
    queryKey: ['admin', 'deliveryZones'],
    queryFn: async () => {
      const result = await graphqlRequest<{ deliveryZones: DeliveryZoneFull[] }>(`
        query DeliveryZones {
          deliveryZones {
            ${DELIVERY_ZONE_FRAGMENT}
          }
        }
      `)
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deliveryZones ?? []
    },
  })
}

export function useDeliveryZone(id: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'deliveryZone', id],
    queryFn: async () => {
      const result = await graphqlRequest<{ deliveryZone: DeliveryZoneFull | null }>(`
        query DeliveryZone($id: UUID!) {
          deliveryZone(id: $id) {
            ${DELIVERY_ZONE_FRAGMENT}
          }
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deliveryZone
    },
    enabled,
  })
}

export function useDeliveryFeeAreas(zoneId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'deliveryFeeAreas', zoneId],
    queryFn: async () => {
      const result = await graphqlRequest<{ deliveryFeeAreas: DeliveryFeeArea[] }>(`
        query DeliveryFeeAreas($zoneId: UUID!) {
          deliveryFeeAreas(zoneId: $zoneId) {
            ${FEE_AREA_FRAGMENT}
          }
        }
      `, { zoneId })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deliveryFeeAreas ?? []
    },
    enabled,
  })
}

export function useDeliveryFeeArea(id: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'deliveryFeeArea', id],
    queryFn: async () => {
      const result = await graphqlRequest<{ deliveryFeeArea: DeliveryFeeArea | null }>(`
        query DeliveryFeeArea($id: UUID!) {
          deliveryFeeArea(id: $id) {
            ${FEE_AREA_FRAGMENT}
          }
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deliveryFeeArea
    },
    enabled,
  })
}

export function useFindDeliveryFeeArea(zoneId: string, lat: number, lng: number, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'findDeliveryFeeArea', zoneId, lat, lng],
    queryFn: async () => {
      const result = await graphqlRequest<{ findDeliveryFeeArea: DeliveryFeeArea | null }>(`
        query FindDeliveryFeeArea($zoneId: UUID!, $lat: Float!, $lng: Float!) {
          findDeliveryFeeArea(zoneId: $zoneId, lat: $lat, lng: $lng) {
            ${FEE_AREA_FRAGMENT}
          }
        }
      `, { zoneId, lat, lng })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.findDeliveryFeeArea
    },
    enabled,
  })
}

// Mutations

export function useCreateDeliveryZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (input: CreateDeliveryZoneInput) => {
      const result = await graphqlRequest<{ createDeliveryZone: DeliveryZoneFull }>(`
        mutation CreateDeliveryZone($input: CreateDeliveryZoneInput!) {
          createDeliveryZone(input: $input) {
            ${DELIVERY_ZONE_FRAGMENT}
          }
        }
      `, { input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createDeliveryZone
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryZones'] })
    },
  })
}

export function useUpdateDeliveryZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateDeliveryZoneInput }) => {
      const result = await graphqlRequest<{ updateDeliveryZone: DeliveryZoneFull }>(`
        mutation UpdateDeliveryZone($id: UUID!, $input: UpdateDeliveryZoneInput!) {
          updateDeliveryZone(id: $id, input: $input) {
            ${DELIVERY_ZONE_FRAGMENT}
          }
        }
      `, { id, input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateDeliveryZone
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryZones'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryZone', id] })
    },
  })
}

export function useDeleteDeliveryZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await graphqlRequest<{ deleteDeliveryZone: boolean }>(`
        mutation DeleteDeliveryZone($id: UUID!) {
          deleteDeliveryZone(id: $id)
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deleteDeliveryZone
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryZones'] })
    },
  })
}

export function useCreateDeliveryFeeArea() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (input: CreateDeliveryFeeAreaInput) => {
      const result = await graphqlRequest<{ createDeliveryFeeArea: DeliveryFeeArea }>(`
        mutation CreateDeliveryFeeArea($input: CreateDeliveryFeeAreaInput!) {
          createDeliveryFeeArea(input: $input) {
            ${FEE_AREA_FRAGMENT}
          }
        }
      `, { input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.createDeliveryFeeArea
    },
    onSuccess: (data) => {
      if (data?.zoneId) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryFeeAreas', data.zoneId] })
      }
    },
  })
}

export function useUpdateDeliveryFeeArea() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateDeliveryFeeAreaInput }) => {
      const result = await graphqlRequest<{ updateDeliveryFeeArea: DeliveryFeeArea }>(`
        mutation UpdateDeliveryFeeArea($id: UUID!, $input: UpdateDeliveryFeeAreaInput!) {
          updateDeliveryFeeArea(id: $id, input: $input) {
            ${FEE_AREA_FRAGMENT}
          }
        }
      `, { id, input })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.updateDeliveryFeeArea
    },
    onSuccess: (data) => {
      if (data?.zoneId) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryFeeAreas', data.zoneId] })
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryFeeArea'] })
    },
  })
}

export function useDeleteDeliveryFeeArea() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id }: { id: string; zoneId: string }) => {
      const result = await graphqlRequest<{ deleteDeliveryFeeArea: boolean }>(`
        mutation DeleteDeliveryFeeArea($id: UUID!) {
          deleteDeliveryFeeArea(id: $id)
        }
      `, { id })
      
      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      return result.data?.deleteDeliveryFeeArea
    },
    onSuccess: (_, { zoneId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deliveryFeeAreas', zoneId] })
    },
  })
}
